import { Body, Button, Heading, Icon } from "@elaniintc/goes-design-system";
import { useParams } from "react-router";
import {
  getFormalityRequestRevisionExpired,
  useGetFormalityRequestDetail,
} from "@/queries/formaly-request";
import { useEffect, useState, useMemo } from "react";
import { useInfo } from "../../hooks/use-info";
import InsolvencyWarningBadge from "./insolvency-warning-badge";
import { useGetExecution } from "@/queries/flows/flows.api";
import dayjs from "dayjs";
import { FormCard, type FormNode } from "./cards/form-card";
import { SingCard } from "./cards/sing-card";
import { PaymentCard } from "../payment/PaymentCard";
import TablePaginantion from "@/components/common/table/pagination";
import { useContinuePaymentRequest } from "@/queries/payment/payment.api";
import { useIsFetching } from "@tanstack/react-query";
import { shouldShowContinueButton as getShouldShowContinueButton } from "@/utils/procedures-ui";
import { CancelationWarning } from "./cancelation-warning";

const allowedNodeTypes = [
  "form-node",
  "sign-document-node",
  "payment-node",
  // "document-generator-node",
];

const ITEMS_PER_PAGE = 20;

export const ProcedureFormsList = () => {
  const params = useParams<{ procedureId: string }>();
  const { setShouldShowInfo, setCanBeCancelled } = useInfo();

  const [isExpired, setIsExpired] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isProcedureCorrupted, setIsProcedureCorrupted] = useState(false);

  const { data: formalityRequest } = useGetFormalityRequestDetail(
    params.procedureId ?? "",
  );

  const {
    data: nodesExecutions,
    isLoading,
    isFetching,
  } = useGetExecution(formalityRequest?.flow_execution_id as string, 5000);

  const isExecutionLoading = isLoading || isFetching;

  useEffect(() => {
    if (nodesExecutions?.data?.node_executions?.some(({ node }) => !node)) {
      setIsProcedureCorrupted(true);
    }
  }, [nodesExecutions]);

  useEffect(() => {
    let mounted = true;

    getFormalityRequestRevisionExpired(params.procedureId!).then((data) => {
      if (!mounted) return;
      if (data.length && isExpired === false) {
        setIsExpired(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, [params.procedureId, isExpired]);

  useEffect(() => {
    setShouldShowInfo(true);
  }, [setShouldShowInfo]);

  // NODE_EXECUTIONS PROCESADOS (sin duplicados)
  const uniqueExecutionNodes = useMemo(() => {
    const executions = nodesExecutions?.data?.node_executions ?? [];

    type Execution = (typeof executions)[number];

    const byId = executions
      .filter((node) => allowedNodeTypes.includes(node?.node?.ref_name || ""))
      .reduce<Record<string, Execution>>((acc, node) => {
        const key = node.node.id;
        const existing = acc[key as string];

        if (node.node.ref_name === "payment-node" && !node.payment_request) {
          //find other payment-node with the same node.node.id that has payment_request not null
          const relatedFormExecution = Object.values(acc).find(
            (paymentExec) =>
              paymentExec.node.id === node.node.id &&
              paymentExec.payment_request,
          );

          if (relatedFormExecution) {
            // Asignar la payment_request directamente al form-node
            acc[relatedFormExecution?.node?.id || ""] = {
              ...node,
              payment_request: relatedFormExecution.payment_request,
            };
          }

          return acc;
        }

        if (!existing) {
          acc[key as string] = node;
          return acc;
        }

        const isCompleted = node.status === "COMPLETED";
        const existingCompleted = existing.status === "COMPLETED";

        let chosen = existing;

        // Regla 1: preferir COMPLETED
        if (isCompleted && !existingCompleted) {
          chosen = node;
        } else if (isCompleted === existingCompleted) {
          // Si los dos son COMPLETED o los dos NO lo son, elegir el más reciente por started_at
          const nodeStarted = node.started_at
            ? dayjs(node.started_at)
            : dayjs(0);
          const existingStarted = existing.started_at
            ? dayjs(existing.started_at)
            : dayjs(0);

          if (nodeStarted.isAfter(existingStarted)) {
            chosen = node;
          }
        }

        // Regla 2: si el elegido no tiene assignments pero el otro sí, usar los del otro
        const other = chosen === node ? existing : node;

        let updatedFormAssignments = chosen.form_assignments;
        const chosenHasFormAssignments =
          chosen.form_assignments && chosen.form_assignments.length > 0;
        const otherHasFormAssignments =
          other.form_assignments && other.form_assignments.length > 0;

        if (!chosenHasFormAssignments && otherHasFormAssignments) {
          updatedFormAssignments = other.form_assignments;
        }

        let updatedUserFormAssignments = chosen.user_form_assignments;
        const chosenHasUserFormAssignments =
          chosen.user_form_assignments &&
          chosen.user_form_assignments.length > 0;
        const otherHasUserFormAssignments =
          other.user_form_assignments && other.user_form_assignments.length > 0;

        if (!chosenHasUserFormAssignments && otherHasUserFormAssignments) {
          updatedUserFormAssignments = other.user_form_assignments;
        }

        if (
          updatedFormAssignments !== chosen.form_assignments ||
          updatedUserFormAssignments !== chosen.user_form_assignments
        ) {
          chosen = {
            ...chosen,
            form_assignments: updatedFormAssignments,
            user_form_assignments: updatedUserFormAssignments,
          };
        }

        acc[key as string] = chosen;
        return acc;
      }, {});

    // sort por orden de ejecución
    return Object.values(byId).sort((a, b) => {
      return a.node.order - b.node.order;
    });
  }, [nodesExecutions]);

  // Verificar si hay un nodo de resolución ejecutado
  useEffect(() => {
    console.log(nodesExecutions?.data?.node_executions);
    const hasExecutedResolutionNode =
      nodesExecutions?.data?.node_executions.some(
        (node) =>
          node?.node?.ref_name === "resolution-node" &&
          node?.status === "COMPLETED",
      );
    setCanBeCancelled(!hasExecutedResolutionNode);
  }, [nodesExecutions, setCanBeCancelled]);

  // Paginación
  const totalItems = uniqueExecutionNodes.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedNodes = uniqueExecutionNodes.slice(startIndex, endIndex);

  const { shouldShowContinueButton, paymentRequestId } = useMemo(
    () =>
      getShouldShowContinueButton(
        uniqueExecutionNodes,
        nodesExecutions?.data?.status,
      ),
    [uniqueExecutionNodes, nodesExecutions?.data?.status],
  );

  const { mutateAsync: continuePayment, isPending: isContinuing } =
    useContinuePaymentRequest(formalityRequest?.flow_execution_id ?? undefined);

  // Verificar si las queries están siendo refetched después de invalidar
  const isRefetchingFlowExecution = useIsFetching({
    queryKey: ["flow-execution", formalityRequest?.flow_execution_id],
  });
  const isRefetchingPaymentRequest = useIsFetching({
    queryKey: ["payment-request", paymentRequestId],
  });
  const isRefetchingQueries =
    (isRefetchingFlowExecution > 0 || isRefetchingPaymentRequest > 0) &&
    !isContinuing;

  // Reset to page 1 when nodes change
  useEffect(() => {
    setCurrentPage(1);
  }, [totalItems]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (isProcedureCorrupted) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-center">
          <Icon
            type="far"
            name="circle-exclamation"
            className="text-warning text-4xl"
          />
        </div>
        <Heading variant="h4" className="text-center">
          Trámite con datos inconsistentes
        </Heading>

        <Body>
          Hemos detectado que este trámite tiene datos inconsistentes que
          impiden mostrar la información correctamente. Por favor, contacta al
          soporte para resolver este problema.
        </Body>
        <Body>
          Una de las posibles razones de esta inconsistencia es que se hayan
          eliminado nodos del trámite después de que se hayan ejecutado. Para
          evitar este tipo de problemas, te recomendamos revisar el trámite y
          asegurarte de no eliminar nodos que ya han sido ejecutados.
        </Body>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div>
        <Heading variant="h6">
          Respuestas de formulario
          {isExecutionLoading && (
            <Icon
              name="spinner-third"
              type="fas"
              className="animate-spin ml-2 text-on-disabled text-sm"
            />
          )}
        </Heading>
        <Body>
          Revisa las respuestas de los formularios realizados en este trámite.
        </Body>
      </div>
      <div className="w-full max-w-[1312px]">
        <InsolvencyWarningBadge
          userId={formalityRequest?.applicant_id ?? ""}
          userName={formalityRequest?.name ?? ""}
        />
      </div>

      <CancelationWarning
        cancellation_requests={formalityRequest?.cancellation_requests}
        formalityCode={formalityRequest?.code || ""}
        requestGroups={formalityRequest?.groups}
      />
      <div className="flex flex-col gap-2 w-full max-w-[1312px]">
        {/* Nuevo listado de nodos – usando paginatedNodes */}
        {paginatedNodes.map((node, index) => {
          const globalIndex = startIndex + index;
          switch (node.node.type) {
            case "FORM":
              return (
                <FormCard
                  key={node.id ?? node.node.id ?? globalIndex}
                  node={node as FormNode}
                  index={globalIndex}
                  procedureId={params.procedureId as string}
                />
              );
            case "SIGN_DOCUMENT":
              return (
                <SingCard
                  key={node.id ?? node.node.id ?? globalIndex}
                  node={node}
                  index={globalIndex}
                  procedureId={params.procedureId as string}
                />
              );
            case "PAYMENT":
              console.log(node.payment_request);
              if (node.payment_request) {
                return (
                  <PaymentCard
                    key={node.id ?? node.node.id ?? globalIndex}
                    paymentRequestId={node.payment_request.id}
                    nodeExecution={node}
                    procedureId={params.procedureId as string}
                    index={globalIndex}
                  />
                );
              }
              break;
            default:
              break;
          }
        })}

        {/* Maquetado del objeto tiempo caducado */}
        {isExpired && (
          <div className="bg-[#FF002C33] border-[0.5px] border-[#FF002C] p-3 rounded-xl">
            <section style={{ display: "flex", gap: 6 }}>
              <div className="flex items-center justify-center border-[0.5px] border-[#FF002C] w-[24px] h-[24px] bg-[#FF002C33] rounded-full">
                <Icon
                  size="xs"
                  type="fas"
                  name="clock"
                  className="text-danger-quiet"
                />
              </div>
              <h2 className="text-md font-semibold text-[#B91C1C]">
                Tu tiempo ha caducado para un paso de esta solicitud
              </h2>
            </section>
            <section className="text-sm text-[#EF4444]">
              Revisa la solicitud y atiende el paso lo antes posible.
            </section>
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="mt-4">
            <TablePaginantion
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
              onNext={handleNext}
              onPrevious={handlePrevious}
              hasNext={currentPage < totalPages}
              hasPrevious={currentPage > 1}
            />
          </div>
        )}

        {shouldShowContinueButton && (
          <div className="mt-4 flex justify-end">
            <Button
              size="lg"
              onClick={async () => {
                if (!paymentRequestId) return;

                await continuePayment(paymentRequestId);
              }}
              isLoading={isContinuing || isRefetchingQueries}
              disabled={
                !paymentRequestId || isContinuing || isRefetchingQueries
              }
            >
              Continuar tramite
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
